import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, tap, filter, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { IS_PUBLIC_API } from '../shared/utils/http-context-tokens';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const accessToken = authService.getAccessToken();

  if (accessToken) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  console.log('Pasando por authInterceptor:', performance.now());

  return next(req).pipe(
    tap((event: HttpEvent<any>) => {
      console.log(`AuthInterceptor: Evento recibido (${event.type}):`);
      console.log('AuthInterceptor:', performance.now());
    }),
    catchError((error) => {
      console.log('interceptor:', req.url);
      console.log('AuthInterceptor:', performance.now());

      if (error.status === 401 && !req.context.get(IS_PUBLIC_API)) {
        // Si es un 401
        if (!authService.getIsRefreshing()) {
          // Si no se está refrescando, iniciar el refresco
          console.log(
            'Interceptor: 401 recibido y no se está refrescando. Iniciando manejo de 401...'
          );
          console.log('AuthInterceptor:', performance.now());
          return handle401Error(req, next, authService, router).pipe(
            catchError((refreshError) => {
              console.log(
                'Interceptor: Error después de intentar refrescar y reintentar:',
                refreshError
              );
              return throwError(() => refreshError);
            })
          );
        } else {
          // Si ya se está refrescando, poner en cola la solicitud
          console.log(
            'Interceptor: 401 recibido pero ya se está refrescando. Poniendo en cola la petición.'
          );
          return authService.getRefreshTokenSubject().pipe(
            filter((token) => token !== null), // Esperar hasta que se emita un nuevo token (o error)
            take(1), // Tomar solo el primer valor emitido
            switchMap((newAccessToken) => {
              if (newAccessToken) {
                // Si se obtuvo un nuevo token, reintentar la solicitud original
                console.log(
                  'Interceptor: Token refrescado. Reintentando petición en cola...'
                );
                req = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newAccessToken}`,
                  },
                });
                return next(req);
              } else {
                // Si el token es null (indicando un error en el refresco), propagar el error original
                console.log(
                  'Interceptor: Refresco de token fallido. Propagando error original.'
                );
                return throwError(() => error);
              }
            }),
            catchError((queueError) => {
              // Capturar errores si el refreshTokenSubject emite un error
              console.log(
                'Interceptor: Error al procesar la cola de refresco:',
                queueError
              );
              return throwError(() => queueError);
            })
          );
        }
      }

      console.log(
        'Interceptor: Error en interceptor (no 401 o ya refrescando):',
        error
      );
      return throwError(() => error);
    })
  );
};

const handle401Error = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
) => {
  console.log(
    'handle401Error: Inicio manejo de 401. Llamando a refreshToken...'
  );

  return authService.refreshToken().pipe(
    tap(() => console.log('handle401Error: refreshToken exitoso.')), // Log de éxito
    switchMap((tokens) => {
      console.log(
        'handle401Error: refreshToken exitoso. Reintentando petición original...'
      );
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${tokens.access_token}`,
        },
      });
      console.log(
        'handle401Error: Petición original clonada con nuevo token. Pasando a next(req)...'
      );
      return next(req).pipe(
        tap((response) =>
          console.log('handle401Error: Petición reintentada exitosa:', response)
        ), // Log de éxito del reintento
        catchError((retryError) => {
          console.log(
            'handle401Error: Error al reintentar la petición:',
            retryError
          );
          return throwError(() => retryError); // Propagar el error del reintento
        })
      );
    }),
    catchError((error) => {
      console.log('handle401Error: Error durante el refreshToken:', error); // Log específico para error de refresh
      authService.logout();
      return throwError(() => error);
    })
  );
};
